import { log as debugLog } from '@/utils/logger'
import { toUtcSeconds } from '@/utils/utcTime'
import BadgeComponent from './Badge.jsx'
import PanelComponent from './Panel.jsx'
import { BoxFillSeries } from '@/indicators/ict_quantum/BoxFillSeries'
import { LabelOverlaySeries } from '@/indicators/ict_quantum/LabelOverlaySeries'
import { mtfSuffix, tfSeconds } from '@/utils/mtf'

// ─── Helpers de tiempo y validación ───────────────────────────────────────────

// Conversor ÚNICO compartido (@/utils/utcTime, v1.3.2): acepta segundos,
// milisegundos (payloads /charting/*), strings 'YYYY-MM-DD' (TFs diarios+) y
// business-day objects de LWC. Antes cleanTime solo convertía ms→s de números
// y los times string de 1D/1W/1M pasaban CRUDOS → comparaciones/Math.max
// número-vs-string en NaN o silencio (nada pintado en diario+). Contrato
// externo intacto: null para lo no interpretable, segundos UTC si no.
function cleanTime(t) {
  return toUtcSeconds(t)
}

function cleanValue(v) {
  if (v == null || Number.isNaN(v) || !Number.isFinite(v)) return null
  return v
}

function timeToSeconds(t) {
  return toUtcSeconds(t)
}

function overlapsVisibleRange(start, end, range) {
  if (!range) return true
  const s = timeToSeconds(start)
  const e = timeToSeconds(end)
  if (s == null || e == null) return false
  return s <= range.to && e >= range.from
}

function sortByTimeDesc(arr, key = 'time') {
  return [...arr].sort((a, b) => (cleanTime(b[key]) ?? 0) - (cleanTime(a[key]) ?? 0))
}

function normalizeDirection(dir) {
  const d = String(dir || '').toLowerCase()
  if (d === 'bull' || d === 'bisi' || d === 'long' || d === 'buy') return 'bull'
  if (d === 'bear' || d === 'sibi' || d === 'short' || d === 'sell') return 'bear'
  return null
}

function gapSizePercent(top, bottom) {
  const size = Math.abs(top - bottom)
  const mid = (Math.abs(top) + Math.abs(bottom)) / 2
  if (!mid || mid <= 0 || !Number.isFinite(size)) return 0
  return (size / mid) * 100
}

// ─── Datos externos (inyectados desde ChartPage / ChartPublicPage) ────────────

let externalData = null
let externalVersion = 0

export function setExternalData(data) {
  externalData = data
  externalVersion++
}

export function getExternalData() {
  return externalData
}

export function getExternalVersion() {
  return externalVersion
}

// ─── Configuración por defecto ──────────────────────────────────────────────────

export const defaultConfig = {
  _version: 4,

  // ── Visibilidad ──────────────────────────────────────────────────────────
  showBullFVG: true,
  showBearFVG: true,
  showIFVG: true,
  showLabels: true,

  // ── Densidad ─────────────────────────────────────────────────────────────
  maxVisibleZones: 10,

  // ── Lógica (tolerante ge=0 en backend) ────────────────────────────────────
  gapFilter: 0.5,
  fillMode: 'Close',
  mitigationPct: 50,
  ifvgLookback: 5,

  // ── Tamaño mínimo del gap (% del precio medio de la zona). 0 = mostrar todo.
  minGapSizePercent: 0,
  minIfvgSizePercent: 0,

  // ── Extensión de caja (barras). Misma regla para FVG e IFVG.
  boxLengthBars: 50,

  // ── Estilo ───────────────────────────────────────────────────────────────
  fillOpacity: 0.10,
  ifvgFillOpacity: 0.06,
  borderWidth: 1,

  // ── Paleta Quantum ─────────────────────────────────────────────────────────
  colorBull: '#1B5E20',
  colorBear: '#B71C1C',

  // ── Dashboard ────────────────────────────────────────────────────────────
  showDashboard: true,
  dashboardMinimized: false,
  badgePosition: { x: 8, y: 8 },
  panelSections: {},

  // ── MTF: timeframes adicionales (solo mayores que el del chart, máx. 2) ──
  // Vacío = MTF OFF (default). La selección se sanea contra el TF del chart
  // en cada fetch/render; un TF que deja de ser mayor simplemente no se pide.
  htfTimeframes: [],
}

// ─── detect() ─────────────────────────────────────────────────────────────────

export function detect(candles, config) {
  const t0 = typeof performance !== 'undefined' ? performance.now() : 0
  if (!externalData) {
    const t1 = typeof performance !== 'undefined' ? performance.now() : 0
    debugLog(`[fvg_ifvg_quantum] detect empty: ${(t1 - t0).toFixed(2)}ms`)
    return null
  }
  const result = {
    ...externalData,
    _dataVersion: externalVersion,
  }
  const t1 = typeof performance !== 'undefined' ? performance.now() : 0
  debugLog(`[fvg_ifvg_quantum] detect from external: ${(t1 - t0).toFixed(2)}ms`)
  return result
}

// ─── render() ─────────────────────────────────────────────────────────────────

export function render(chart, candleSeries, result, config, api) {
  const t0 = typeof performance !== 'undefined' ? performance.now() : 0
  if (!result) return

  const cfg = { ...defaultConfig, ...(config || {}) }

  const allCandles = candleSeries && candleSeries.data ? candleSeries.data() : []
  const lastCandle = allCandles.length > 0 ? allCandles[allCandles.length - 1] : null
  const lastTime = lastCandle ? lastCandle.time : null
  const activeEndTime = lastTime != null ? timeToSeconds(lastTime) : null

  // Ventana de DATOS cargada — fija e independiente del viewport (misma regla
  // que Order Blocks: la detección corre server-side sobre esa ventana y el
  // viewport solo clipea visualmente). Las zonas se filtran contra ella, no
  // contra el rango visible: una zona pintada jamás aparece/desaparece al
  // hacer pan y los contadores del badge son estables (badge == pintadas).
  const firstCandleTime = allCandles.length > 0 ? cleanTime(allCandles[0].time) : null
  const lastCandleTime = allCandles.length > 0 ? cleanTime(allCandles[allCandles.length - 1].time) : null
  const dataWindow = firstCandleTime != null && lastCandleTime != null
    ? { from: firstCandleTime, to: lastCandleTime }
    : null

  const labelsByTime = {}
  const addLabel = (opts) => {
    const t = opts?.time
    if (t == null || Number.isNaN(t)) return
    if (!labelsByTime[t]) labelsByTime[t] = []
    labelsByTime[t].push(opts)
  }

  const renderedZones = []
  const renderedLabels = []

  // ── FVGs (BISI / SIBI) ─────────────────────────────────────────────────────
  const fvgCandidates = []
  const minGapPct = Math.max(0, Number(cfg.minGapSizePercent) || 0)
  if (cfg.showBullFVG || cfg.showBearFVG) {
    for (const f of result.fvgs || []) {
      // Zonas mitigadas/llenas no se pintan.
      if (f.mitigated || f.filled) continue

      const start = cleanTime(f.start_time)
      const top = cleanValue(f.top)
      const bottom = cleanValue(f.bottom)
      if (start == null || top == null || bottom == null) continue

      const direction = normalizeDirection(f.direction)
      if (direction === 'bull' && !cfg.showBullFVG) continue
      if (direction === 'bear' && !cfg.showBearFVG) continue
      if (!direction) continue

      if (minGapPct > 0 && gapSizePercent(top, bottom) < minGapPct) continue

      const end = cleanTime(f.end_time) ?? activeEndTime
      if (end == null || end < start) continue
      if (!overlapsVisibleRange(start, end, dataWindow)) continue

      // MTF: las zonas de timeframes adicionales vienen marcadas con `_mtf`
      // por el merge de la página (ver utils/mtf.js). Regla visual canónica:
      // sólido = FVG propio, dashed = IFVG propio, DOTTED = HTF SIEMPRE (en
      // HTF no se usa dashed aunque sea IFVG — el tipo viaja en la etiqueta).
      const mtf = f._mtf && tfSeconds(f._mtf) != null ? f._mtf : null

      const color = direction === 'bull' ? cfg.colorBull : cfg.colorBear
      fvgCandidates.push({
        start,
        end,
        top,
        bottom,
        color,
        // Relleno HTF: misma familia de color con opacidad reducida ~50%.
        alpha: mtf ? cfg.fillOpacity * 0.5 : cfg.fillOpacity,
        borderWidth: cfg.borderWidth,
        borderStyle: mtf ? 'dotted' : 'solid',
        label: (direction === 'bull' ? 'BISI' : 'SIBI') + (mtf ? mtfSuffix(mtf) : ''),
        type: 'FVG',
        direction,
        mtf,
      })
    }
  }

  const sortedFvgs = sortByTimeDesc(fvgCandidates, 'start')
  const limitedFvgs = sortedFvgs.slice(0, Math.max(0, cfg.maxVisibleZones ?? 10))

  // ── IFVGs ──────────────────────────────────────────────────────────────────
  const ifvgCandidates = []
  const minIfvgPct = Math.max(0, Number(cfg.minIfvgSizePercent) || 0)
  if (cfg.showIFVG) {
    for (const f of result.ifvgs || []) {
      const start = cleanTime(f.start_time)
      const top = cleanValue(f.top)
      const bottom = cleanValue(f.bottom)
      if (start == null || top == null || bottom == null) continue

      const direction = normalizeDirection(f.direction) || 'bear'
      const end = cleanTime(f.end_time) ?? activeEndTime
      if (end == null || end < start) continue
      if (!overlapsVisibleRange(start, end, dataWindow)) continue

      if (minIfvgPct > 0 && gapSizePercent(top, bottom) < minIfvgPct) continue

      // MTF: misma regla que los FVG — HTF siempre dotted (el dashed está
      // reservado al IFVG del TF propio) y relleno atenuado ~50%.
      const mtf = f._mtf && tfSeconds(f._mtf) != null ? f._mtf : null

      const color = direction === 'bull' ? cfg.colorBull : cfg.colorBear
      ifvgCandidates.push({
        start,
        end,
        top,
        bottom,
        color,
        alpha: mtf ? cfg.ifvgFillOpacity * 0.5 : cfg.ifvgFillOpacity,
        borderWidth: cfg.borderWidth,
        borderStyle: mtf ? 'dotted' : 'dashed',
        label: 'IFVG' + (mtf ? mtfSuffix(mtf) : ''),
        type: 'IFVG',
        direction,
        mtf,
      })
    }
  }

  const sortedIfvgs = sortByTimeDesc(ifvgCandidates, 'start')
  const limitedIfvgs = sortedIfvgs.slice(0, Math.max(0, cfg.maxVisibleZones ?? 10))

  // Orden de pintado: las zonas HTF primero (a TF mayor, más al fondo) y las
  // del TF propio encima — la estructura grande queda detrás y la del
  // timeframe activo manda visualmente (misma regla que Order Blocks).
  const allCandidates = [...limitedFvgs, ...limitedIfvgs]
  const htfCandidates = allCandidates
    .filter((z) => z.mtf)
    .sort((a, b) => ((tfSeconds(b.mtf) ?? 0) - (tfSeconds(a.mtf) ?? 0)) || (b.start - a.start))
  const ownCandidates = allCandidates.filter((z) => !z.mtf)
  const paintOrder = [...htfCandidates, ...ownCandidates]

  // ── Pintar zonas y etiquetas ───────────────────────────────────────────────
  const paintZone = (zone) => {
    const { start, end, top, bottom, color, alpha, borderWidth, borderStyle, label, type, direction, mtf } = zone

    const seriesData = [
      { time: start, upper: top, lower: bottom, fillColor: color, color, alpha, borderColor: color, borderWidth, borderStyle },
      { time: end, upper: top, lower: bottom, fillColor: color, color, alpha, borderColor: color, borderWidth, borderStyle },
    ]

    const boxSeries = api.addCustomSeries(new BoxFillSeries(), {
      color: 'rgba(0,0,0,0)',
      fillColor: 'rgba(0,0,0,0)',
      lineWidth: 0,
      lastValueVisible: false,
      priceLineVisible: false,
      borderWidth,
      borderStyle,
      alpha,
      autoscaleInfoProvider: () => null,
    })
    if (boxSeries) boxSeries.setData(seriesData)

    if (cfg.showLabels && label) {
      addLabel({
        time: start,
        price: top,
        text: label,
        color,
        textColor: color,
        anchor: 'above',
        offset: 6,
        priority: 5,
        plaqueStyle: 'plain',
      })
      renderedLabels.push({ time: start, price: top, text: label, type, direction, mtf })
    }

    renderedZones.push({ start, end, top, bottom, type, direction, color, alpha, borderStyle, mtf })
  }

  for (const z of paintOrder) paintZone(z)

  // Contadores de lo REALMENTE pintado — única fuente del badge, para que
  // badge y canvas cuadren 1:1 en cualquier config (toggles, min gap, límites…).
  // Los contadores clásicos solo cuentan el TF PROPIO; las zonas HTF van
  // aparte, por TF, para la línea "HTF: 4H ×3 · 1D ×1" del badge.
  const ownZones = renderedZones.filter((z) => !z.mtf)
  const htfCounts = {}
  for (const z of renderedZones) {
    if (z.mtf) htfCounts[z.mtf] = (htfCounts[z.mtf] || 0) + 1
  }
  result._paintedCounts = {
    fvg_bull: ownZones.filter((z) => z.type === 'FVG' && z.direction === 'bull').length,
    fvg_bear: ownZones.filter((z) => z.type === 'FVG' && z.direction === 'bear').length,
    ifvg_bull: ownZones.filter((z) => z.type === 'IFVG' && z.direction === 'bull').length,
    ifvg_bear: ownZones.filter((z) => z.type === 'IFVG' && z.direction === 'bear').length,
    total: ownZones.length,
    htf: htfCounts,
  }

  // ── Etiquetas de texto plano en canvas (una sola capa, frame a frame) ──────
  if (Object.keys(labelsByTime).length > 0) {
    const labelSeries = api.addCustomSeries(new LabelOverlaySeries(), {
      color: 'transparent',
      lineWidth: 0,
      lastValueVisible: false,
      priceLineVisible: false,
      crosshairMarkerVisible: false,
      autoscaleInfoProvider: () => null,
      priceScaleId: 'right',
      plaqueStyle: 'plain',
      font: '10px -apple-system, BlinkMacSystemFont, "Trebuchet MS", Roboto, Ubuntu, sans-serif',
    })
    if (labelSeries) {
      const labelData = Object.keys(labelsByTime)
        .map(t => ({ time: Number(t), labels: labelsByTime[t] }))
        .sort((a, b) => a.time - b.time)
      labelSeries.setData(labelData)
    }
  }

  const t1 = typeof performance !== 'undefined' ? performance.now() : 0
  debugLog(`[fvg_ifvg_quantum] render done: ${(t1 - t0).toFixed(2)}ms`)

  // Exponer datos de render para verificación programática externa (Playwright).
  if (typeof window !== 'undefined') {
    window.__fvgIfvgRender = {
      zones: renderedZones,
      labels: renderedLabels,
      counts: result._paintedCounts,
      timestamp: Date.now(),
    }
  }
}

export default {
  id: 'fvg_ifvg_quantum',
  name: 'FVG / IFVG Quantum',
  version: '1.1.0',
  // Nada de este render depende del viewport: las primitivas son nativas del
  // chart y se desplazan con el pan. Opt-out del repintado por panTick para
  // que las zonas no parpadeen al arrastrar (anti-flicker, regla OB).
  renderOnPan: false,
  defaultConfig,
  detect,
  render,
  BadgeComponent,
  PanelComponent,
}
